import {cdnImage, cdnUrl} from '@/util/cdnImage';
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import {WHITE_LABEL_NFT_DATA} from '@/util/metaData/nft';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import Link from 'next/link';
import {spacing} from '@/styles/tokens';

// Above-the-fold sections are imported statically, not with next/dynamic: a dynamic section renders
// nothing until its chunk arrives after an in-site navigation, then pushes the page down (CLS).
import Banner from '@/components/Ui-components/Banner';
import OneStepSolution from '@/components/Ui-components/one-step-solution';

const shortbanner = cdnImage('nft-white-label-images/Nft-short-banner-img.webp');

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {cryptoWalletFAQData, nftwhitelabelServices, nftwhitelabelStepData, nftwhitelabelWallet} = await import(
        '@/util/contents/white-nft-marketplace'
    );

    return {
        props: {
            cryptoWalletFAQData,
            nftwhitelabelServices,
            nftwhitelabelStepData,
            nftwhitelabelWallet
        }
    };
};

const CryptoWallet = dynamic(() => import('@/components/Ui-components/cryptowallets'));
const OurServices = dynamic(() => import('@/components/Ui-components/our-services'));
const Questions = dynamic(() => import('@/components/plain-sections/Questions'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const LetDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));

// Migrated from globals.scss `.concept_to_creation_section` (base + nested `.sub-heading-box`
// override on OurServices' own heading block; sole consumer).
const conceptToCreationSectionSx = {
    backgroundColor: '#f7f7f7',
    '& .sub-heading-box': {padding: '0px 0px 30px 0px'}
} as const;

// Migrated from globals.scss `.dummy-box` (sole consumer).
const dummyBoxSx = {paddingTop: `${spacing[20]} !important`} as const;

// Migrated from globals.scss `.nft-adv-sub-grid` (+ nested descendant overrides; sole consumer).
const nftAdvSubGridSx = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    height: '100%',
    '& div': {height: '100% !important'},
    '& div .nft-grid-img': {height: '100% !important'},
    '@media (max-width: 430px)': {'& .nft-grid-img': {display: 'none !important'}}
} as const;

// Migrated from globals.scss `.nft-grid-img` (sole consumer; ancestor override above wins here).
const nftGridImgSx = {width: '100% !important', height: 'auto !important'} as const;

// Migrated from globals.scss `.nft-marketplace-discuss-wrapper` — page-scoped override of the
// shared Lets-discuss component's own `.webdev-background` wrapper (sole consumer).
const nftMarketplaceDiscussWrapperSx = {
    '@media (max-width: 430px)': {'& .webdev-background': {marginTop: '0px !important'}}
} as const;

const NftWhiteLabel = (props: any) => {
    const {openForm, cryptoWalletFAQData, nftwhitelabelServices, nftwhitelabelStepData, nftwhitelabelWallet} = props;
    return (
        <Box className="white-label-marketplace-parent">
            <PageHead meta={WHITE_LABEL_NFT_DATA} url="/white-label-nft-marketplace" />

            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="White Label NFT Marketplace Solution"
                subheading="Get a custom NFT marketplace solution to unlock new revenue streams, and tap into the booming digital asset market with our comprehensive solution. Seamlessly launch, scale, and innovate with White Label NFT Marketplace Software."
                button="Explore Now"
                image={cdnImage('nft-white-label-images/White Label NFT Marketplace Solution.webp')}
                bannerClass="crypto-banner"
                // bannerBackground="crypto-banner-background"
                alt="White Label NFT Marketplace Solution"
            />
            <Box className="one-step white_label_one_step_section top-space">
                <MUIContainer maxWidth="lg">
                    <OneStepSolution
                        sectionhead="Experience NFT Revolution With Our Comprehensive White Label Marketplace Solutions"
                        subhead="Discover the limitless potential of your NFT ecosystem and elevate your digital asset offerings to new heights. Explore our feature-rich solution and embark on a transformative NFT journey today."
                        OneStepData={nftwhitelabelStepData}
                        className="white-label-nft-solution"
                    />

                    <Typography className="one-step-footer">
                        Drive Community Engagement with Our Community-Centric NFT Marketplace Solutions
                    </Typography>
                    <Grid container justifyContent="center">
                        <Box className="spectrum-btn">
                            <Link href="/contact-us" className="menu-links">
                                <MUIButton
                                    label="Contact Us Now"
                                    variant="contained"
                                    className="helper-card-button"
                                    aria-label="Book Consultation"
                                />
                            </Link>
                        </Box>
                    </Grid>
                </MUIContainer>
            </Box>

            <Box className="bottom-spacing white-label-cryptowallet-section">
                <MUIContainer maxWidth="lg">
                    <CryptoWallet
                        heading="Embrace the NFT Revolution: Empowering Diverse Audiences with Innovative NFT Marketplace Solutions"
                        data={nftwhitelabelWallet}
                        className="white-label-cryptowallet"
                    />
                </MUIContainer>
            </Box>

            <Box className="fintech-adv-wrapper bottom-spacing">
                <MUIContainer maxWidth="lg">
                    <Grid container>
                        <Grid item xs={12} sm={12} md={7} lg={7}>
                            <Box sx={dummyBoxSx}>
                                <Typography className="fintech-adv">
                                    Explore the possibilities of our <span>white label NFT marketplace solution </span>and unlock your
                                    digital asset potential today.
                                </Typography>
                                <Box className="spectrum-btn">
                                    <Link href="/contact-us" className="menu-links">
                                        <MUIButton
                                            label="Contact Us Now"
                                            variant="contained"
                                            className="nft-button"
                                            aria-label="Book Consultation"
                                        />
                                    </Link>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={12} md={5} lg={5} sx={nftAdvSubGridSx}>
                            <Box>
                                <DisplayImage src={shortbanner} alt="nftwhitelabel" sx={nftGridImgSx} />
                            </Box>
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Box>
            <Box className="top-space" sx={conceptToCreationSectionSx}>
                <OurServices
                    heading="From Concept to Creation: Building Your NFT Marketplace Development Process Made Easy"
                    subheading="Whether you are an artist, creator, brand, or entrepreneur looking to capitalize on the NFT revolution, our development process is designed to bring your ideas to life. We prioritize collaboration, transparency, and efficiency, ensuring that your marketplace is built with precision and attention to detail."
                    ourServices={nftwhitelabelServices}
                    className="marketplace_services"
                />
                <Typography className="one-step-footer">
                    Monetize Artistic Assets with Tailored NFT Art Marketplace Development Now!
                </Typography>
                <Grid container justifyContent="center" className="bottom-spacing">
                    <Box className="spectrum-btn">
                        <MUIButton
                            label="Let's Get Started"
                            variant="contained"
                            className="helper-card-button"
                            aria-label="Book Consultation"
                            onClick={openForm}
                        />
                    </Box>
                </Grid>
            </Box>

            <Grid item xs={12} className="bottom-spacing" sx={nftMarketplaceDiscussWrapperSx}>
                <LetDiscuss
                    heading="Let’s discuss your project"
                    subheading=" Share your ideas and vision with us, and we can help you turn them into impactful software products,
                                platforms and businesses"
                    btnLabel="Get in touch with us"
                    openForm={openForm}
                />
            </Grid>
            <Box className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={cryptoWalletFAQData} />
                </MUIContainer>
            </Box>

            <Grid item xs={12}>
                <Box className="top-space mobile-top-padding bg-blue-background">
                    <Testimonials heading="Our Clients Describe Us Best" />
                </Box>
            </Grid>

            <Grid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="22" />
            </Grid>

            <script
                id="app-id-json"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org/',
                        '@type': 'Product',
                        name: 'White Label NFT Marketplace',
                        image: cdnUrl('nft-white-label-images/White Label NFT Marketplace Solution.webp'),
                        description:
                            'Discover, buy, and sell unique digital assets with our White Label NFT Marketplace - A streamlined platform for creators and collectors alike.',
                        brand: {
                            '@type': 'Brand',
                            name: 'Debut Infotech Pvt Ltd'
                        },
                        aggregateRating: {
                            '@type': 'AggregateRating',
                            ratingValue: '4.8',
                            bestRating: '5',
                            worstRating: '1',
                            ratingCount: '326'
                        }
                    })
                }}
            />
        </Box>
    );
};
export default NftWhiteLabel;
NftWhiteLabel.Layout = 'Main';
